import axios from 'axios';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { BASE_URL } from 'utils/constants/Api';
import { useTags } from 'utils/hooks';

import { Flex, styled } from '@project44/manifest-ds';
import {
  IUnmatchedIdentifiers,
  ShipmentIdentifierFilter,
  UnmatchedIdentifiersDto,
} from '@project44-models/shipment-list';

import Tag from './Tag';

const TAG_HEIGHT = 25;

const fetchUnmatchedTerms = async (data: ShipmentIdentifierFilter) => {
  const res = await axios.post<IUnmatchedIdentifiers>(
    `${BASE_URL}/shipments/unmatched-identifiers`,
    {
      shipmentIdentifiers: [data],
      groupByField: 'SHIPMENT_IDENTIFIER',
    },
    { withCredentials: true },
  );

  return new UnmatchedIdentifiersDto(res.data);
};

const Input = styled('input', {
  border: 'none',
  flex: '1 1 0',
  minWidth: '50px',
  outline: 0,
  padding: 0,
});

const Container = styled(Flex, {
  border: '1px solid',
  borderColor: '$palette-indigo-500',
  borderRadius: '$small',
  maxHeight: `${TAG_HEIGHT * 4 + 24}px`,
  width: '100%',
  minHeight: '40px',
  variants: {
    disabled: {
      true: {
        borderColor: '$palette-grey-400',
        backgroundColor: '$palette-grey-100',
      },
    },
  },
});

interface IBulkInput {
  identifierType?: ShipmentIdentifierFilter['type'];
  onChange?: (nextValue: string[]) => void;
  initialValue?: string[];
  disabled?: boolean;
}

export default function BulkInput({
  onChange,
  identifierType,
  initialValue,
  disabled,
}: IBulkInput) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [term, setTerm] = useState<string>('');

  const requestFn = useCallback(
    (terms) =>
      fetchUnmatchedTerms({
        values: terms,
        operator: 'EQUALS',
        type: identifierType,
      }),
    [identifierType],
  );

  const { tags, setTags, removeTag, appendTags, getTagStatus } = useTags({
    requestFn,
    onChange,
    initialValue,
  });

  const handleAppendTags = (nextValue: string) => {
    if (nextValue) {
      setTerm('');
      const splitValues = nextValue
        .split(',')
        .map((word) => word.trim())
        .filter(Boolean);
      appendTags(splitValues);
    }
  };

  const handleChangeTerm = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    if (/,/.test(nextValue)) {
      handleAppendTags(nextValue);
      return;
    }
    setTerm(nextValue);
  };

  const handleRemoveTag = (tag: string) => {
    return () => {
      removeTag(tag);
    };
  };

  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      const shouldDeleteLastTag =
        !inputRef.current?.value && document.activeElement === inputRef.current;

      if (event.key === 'Backspace' && shouldDeleteLastTag) {
        event.preventDefault();
        setTags((prev) => prev.slice(0, -1));
      }
    };

    document.addEventListener('keydown', keyDownHandler);

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [setTags]);

  useEffect(() => {
    if (!initialValue?.length) {
      setTags([]);
    }
  }, [initialValue?.length, setTags]);

  return (
    <Container align="center" data-testid="bulk-input" disabled={disabled}>
      <Flex
        css={{
          gap: '4px',
          height: '100%',
          width: '100%',
          padding: '4px',
          overflowY: 'auto',
        }}
        align="center"
        wrap
      >
        {tags.map((tag) => (
          <Tag
            key={tag}
            title={tag}
            onRemove={handleRemoveTag(tag)}
            status={getTagStatus(tag)}
          />
        ))}
        <Input
          ref={inputRef}
          type="text"
          maxLength={1000}
          onChange={handleChangeTerm}
          value={term}
          onBlur={() => {
            handleAppendTags(term);
          }}
          placeholder={!tags.length ? 'comma separated' : undefined}
          disabled={disabled}
        />
      </Flex>
    </Container>
  );
}
